import type { ResponseListItem } from '../types/index.js';
import { EngineBadge } from './EngineBadge.js';
import { ResponseRegionBadge } from './RegionLabels.js';
import { Row, RowFacts, RowFact, RowSources } from './ui/Row.js';

interface ResponseCardProps {
  response: ResponseListItem;
}

function stripMarkdown(text: string): string {
  return text
    .replace(/#{1,6}\s+/g, '')        // headings
    .replace(/\*\*([^*]+)\*\*/g, '$1') // bold
    .replace(/\*([^*]+)\*/g, '$1')     // italic
    .replace(/`([^`]+)`/g, '$1')       // code
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // links
    .replace(/[-*+]\s/g, '')           // list markers
    .replace(/\n{2,}/g, ' ')          // collapse newlines
    .trim();
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}

/**
 * Spec 235 task 5 — ResponseCard, rebuilt on the Row family.
 *
 * This was the whole-card `<Link>` Row.tsx's own header comment describes:
 * one anchor wrapping engine badge + timestamp + prompt + snippet + every
 * quick metric, giving it an accessible name that was all of that
 * concatenated, and making a future nested control (e.g. a per-metric
 * action) invalid HTML. `to`/`title` now put exactly one anchor around the
 * prompt alone, stretched over the row in CSS; everything else here is
 * `top`/`children`, DOM siblings of that anchor.
 *
 * Gate 213.A.3 greps THIS FILE's source for the literal strings
 * `line-clamp-2` and `title={prompt_snippet` — spec 213's fix for a prompt
 * that could silently overflow once the API stopped truncating it server
 * side. `.hub-row__title` already clamps to 2 lines in CSS (primitives.css),
 * which would satisfy the gate's SUBJECT (the prompt is visually
 * constrained) while deleting its PROXY (the two literal strings the gate
 * actually checks for) — a gate failing on genuinely correct code. Both are
 * kept below, redundant with the CSS clamp on purpose: removing them "as
 * dead weight" reintroduces exactly the invisible-until-a-long-prompt-ships
 * defect spec 213 fixed.
 */
export function ResponseCard({ response }: ResponseCardProps) {
  const {
    engine,
    prompt_snippet,
    response_snippet,
    captured_at,
    brand_mentioned,
    sentiment_label,
    mention_position,
    cited_domains,
    region_code,
    region_mechanism,
    region_fidelity,
  } = response;

  return (
    <Row
      to={`/responses/${response.id}`}
      data-testid="response-card"
      title={
        <span
          className="line-clamp-2"
          title={prompt_snippet || undefined}
          data-testid="prompt-text"
        >
          {prompt_snippet || 'No prompt available'}
        </span>
      }
      top={
        <>
          <EngineBadge engine={engine} />
          <ResponseRegionBadge
            regionCode={region_code}
            mechanism={region_mechanism}
            fidelity={region_fidelity}
          />
          <time
            className="hub-row__time"
            dateTime={captured_at}
            title={new Date(captured_at).toLocaleString()}
          >
            {formatRelativeTime(captured_at)}
          </time>
        </>
      }
    >
      <p className="hub-row__snippet" data-testid="response-snippet">
        {response_snippet ? stripMarkdown(response_snippet) : 'No response content'}
      </p>

      {/* Source favicon badges (Spec 050). The only sanctioned exception to
          monochrome in this spec: the <img> itself keeps colour. The
          per-domain-type coloured RING around it does not — RowSources draws
          a plain monochrome disc and keeps the domain type in the tooltip,
          same information, no bespoke Tailwind colour map to maintain. */}
      <RowSources domains={cited_domains ?? []} />

      {/* Quick metrics strip. Sentiment dot and mention-position number lose
          their raw Tailwind colours here (bg-green-400/bg-yellow-400/…,
          text-violet-600) and become plain RowFacts — a measurement, not a
          status; a value that must read as a genuine status belongs in
          `top` as a Chip tone, not a bespoke colour in this row. */}
      <RowFacts data-testid="quick-metrics">
        <RowFact
          icon={brand_mentioned ? 'bx-check' : brand_mentioned === false ? 'bx-x' : 'bx-minus'}
          title="Brand mentioned"
        >
          {brand_mentioned === true ? 'Mentioned' : brand_mentioned === false ? 'Not mentioned' : 'Pending'}
        </RowFact>
        <RowFact icon="bx-happy-heart-eyes" title={`Sentiment: ${sentiment_label ?? 'pending'}`}>
          <span className="capitalize">{sentiment_label ?? 'Pending'}</span>
        </RowFact>
        {mention_position !== null && mention_position !== undefined && (
          <RowFact icon="bx-hash" title="Mention position">
            #{mention_position}
          </RowFact>
        )}
      </RowFacts>
    </Row>
  );
}
